// src/hooks/useRijenState.js
import { useEffect, useMemo, useReducer } from "react";
import { onSnapshot, colRefs } from "../services/firestore";
import { logger } from "../utils/logger"; // voor error logging
import { compareRijNumbers, keyForRowLike } from "../utils/rijHelpers";
import { upgradedWorkflowEnabled } from "../config/runtimeFeatures.js";

// structuur van logger is al aanwezig in project. :contentReference[oaicite:1]{index=1}

function reducer(state, action) {
  switch (action.type) {
    case "UPDATE_RIJEN_KISTEN": {
      const rijen = action.payload.rijen || [];
      return {
        ...state,
        // bestaande map op r.id (laat dit staan voor je UI/andere code)
        rijenMap: new Map(rijen.map((r) => [r.id, { ...r }])),
        // centrale sleutelvorm voor alle machine/lookups
        rijenKeyMap: new Map(rijen.map((r) => [keyForRowLike(r), { ...r }])),
        gescandeKisten: action.payload.gescandeKisten ?? state.gescandeKisten,
      };
    }

    case "UPDATE_KISTEN":
      return { ...state, gescandeKisten: action.payload };
    case "UPDATE_HISTORIEK":
      return { ...state, historiek: action.payload };
    case "SET_READY":
      return {
        ...state,
        ...action.payload, // zet rijenReady of kistenReady
      };

    default:
      return state;
  }
}

export default function useRijenState({
  enabled,
  coolingRowsEnabled = enabled,
  inventoryEnabled = true,
}) {

  // enabled = bv. !!currentUser (pas luisteren na login)
  const [{ rijenMap, rijenKeyMap, gescandeKisten, historiek, rijenReady, kistenReady }, dispatch] =
    useReducer(reducer, {
      rijenMap: new Map(),     // wordt gevuld door state/koeling
      rijenKeyMap: new Map(),
      gescandeKisten: [],      // actief in inventory
      historiek: [],           // logs
      rijenReady: false,
      kistenReady: false,
    });

  // 1) state/koeling → rijenMap + rijenReady
  useEffect(() => {
    if (!enabled || !coolingRowsEnabled) {
      dispatch({ type: "UPDATE_RIJEN_KISTEN", payload: { rijen: [] } });
      dispatch({ type: "SET_READY", payload: { rijenReady: false } });
      return;
    }
    const usesRowDocuments = upgradedWorkflowEnabled;
    const unsub = onSnapshot(
      usesRowDocuments ? colRefs.coolingRows() : colRefs.stateDoc(),
      (snap) => {
        const rijen = usesRowDocuments
          ? snap.docs
              .map((document) => ({ id: document.id, ...document.data() }))
              .sort(compareRijNumbers)
          : snap.exists()
            ? (snap.data().rijen || []).map((row) => ({ ...row }))
            : [];
        dispatch({ type: "UPDATE_RIJEN_KISTEN", payload: { rijen } });
        dispatch({ type: "SET_READY", payload: { rijenReady: true } });
      },
      (err) => logger.error("koelcelrijen lezen mislukt", { err })
    );
    return () => unsub();
  }, [coolingRowsEnabled, enabled]);

  // 2) /inventory → gescandeKisten + kistenReady
  useEffect(() => {
    if (!enabled) {
      dispatch({ type: "UPDATE_KISTEN", payload: [] });
      dispatch({ type: "SET_READY", payload: { kistenReady: false } });
      return;
    }

    if (!inventoryEnabled) {
      dispatch({ type: "UPDATE_KISTEN", payload: [] });
      dispatch({ type: "SET_READY", payload: { kistenReady: true } });
      return;
    }

    const unsub = onSnapshot(
      colRefs.kisten(),
      (q) => {
        const actief = q.docs.map((d) => d.data());
        dispatch({ type: "UPDATE_KISTEN", payload: actief });
        dispatch({ type: "SET_READY", payload: { kistenReady: true } });
      },
      (err) => logger.error("/kisten read error", { err })
    );

    return () => unsub();
  }, [enabled, inventoryEnabled]);



  // convenience: maak array van rijen uit de map
  const rijenArray = useMemo(() => Array.from(rijenMap.values()), [rijenMap]);

  return {
    // reading
    rijenMap,
    rijenKeyMap,
    rijenArray,
    gescandeKisten,
    historiek,
    rijenReady,
    kistenReady,
  };
}
